import type CalendarFolderTypeEnum from 'owa-service/lib/contract/CalendarFolderTypeEnum';
import { isLinkedCalendarEntry } from './isLinkedCalendarEntry';
import type { CalendarEntry, LocalCalendarEntry, LinkedCalendarEntryV2 } from 'owa-graph-schema';
import { isCapabilityEnabled } from 'owa-capabilities';
import { mailboxCalendarModificationCapability } from 'owa-capabilities-definitions/lib/mailboxCalendarModificationCapability';

// We do not want to show 'Rename' and 'Remove' options for "well known" folders like default calendar and birthday calendar
// We will honor the server setting of IsRemovable to whether show or hide the option.
// For birthday and schedule, server sets the IsRemovable setting to true, hence we check their folder type.
// We don't want to show these for Group Mailbox Calendars either, and IsRemovable property doesn't exist on LinkedCalendarEntry,
// which is what we have for Group Calendars, we will make an explicit check for them.
export function canDeleteCalendar(
    calendarEntry: LocalCalendarEntry | LinkedCalendarEntryV2 | undefined
): boolean {
    return calendarEntry
        ? canDeleteLocalCalendar(calendarEntry as LocalCalendarEntry) ||
              canDeleteTeamRhythmCalendar(calendarEntry as LinkedCalendarEntryV2)
        : false;
}

function canDeleteLocalCalendar(calendarEntry: LocalCalendarEntry): boolean {
    return (
        (calendarEntry.IsRemovable == null || calendarEntry.IsRemovable) &&
        (calendarEntry.CalendarFolderType == null || calendarEntry.CalendarFolderType != 3) &&
        !calendarEntry.IsGroupMailboxCalendar &&
        isCapabilityEnabled(
            mailboxCalendarModificationCapability,
            calendarEntry.calendarId.mailboxInfo
        )
    );
}

// For TeamRhythm calendars, we will check the IsRemovable property to determine if we should
// show the delete option, and that is set to true if the user is an owner of the calendar.
function canDeleteTeamRhythmCalendar(calendarEntry: LinkedCalendarEntryV2): boolean {
    return (
        calendarEntry.calendarId.mailboxInfo.type === 'TeamRhythm' && !!calendarEntry.IsRemovable
    );
}

// Prevent the "..." button from showing if user is not allowed to delete the calendar since that
// is the only option we show today; if we add more menu items, update (or remove) this function.
export function canShowCalendarContextMenu(
    calendarEntry: CalendarEntry | LinkedCalendarEntryV2
): boolean {
    return (calendarEntry as LinkedCalendarEntryV2)?.calendarId?.mailboxInfo?.type === 'TeamRhythm'
        ? !!(calendarEntry as LinkedCalendarEntryV2).IsRemovable
        : true;
}

export function canRenameCalendar(calendarEntry: LocalCalendarEntry | undefined): boolean {
    return calendarEntry
        ? canDeleteCalendar(calendarEntry) &&
              !isLinkedCalendarEntry(calendarEntry) &&
              !calendarEntry.IsGroupMailboxCalendar
        : false;
}

export function canChangeCalendarColor(calendarEntry: LocalCalendarEntry | undefined): boolean {
    return calendarEntry
        ? !calendarEntry.IsGroupMailboxCalendar &&
              isCapabilityEnabled(
                  mailboxCalendarModificationCapability,
                  calendarEntry.calendarId.mailboxInfo
              )
        : false;
}

// calendarFolderId is only defined for LocalCalendarEntries, which are the only ones that can set charms
export function canChangeCalendarCharm(calendarEntry: LocalCalendarEntry | undefined): boolean {
    return calendarEntry && !calendarEntry.IsGroupMailboxCalendar
        ? calendarEntry.FolderId != undefined &&
              isCapabilityEnabled(
                  mailboxCalendarModificationCapability,
                  calendarEntry.calendarId.mailboxInfo
              )
        : false;
}

export function canEditCalendar(calendarEntry: LocalCalendarEntry | undefined): boolean {
    return calendarEntry
        ? canRenameCalendar(calendarEntry) ||
              canChangeCalendarColor(calendarEntry) ||
              canChangeCalendarCharm(calendarEntry)
        : false;
}
